import parse, {domToReact, attributesToProps} from "html-react-parser";
import React, {useEffect, useState} from "react";
import {
    Box,
    Heading,
    Image,
    Link,
    Text,
    UnorderedList,
    OrderedList,
    ListItem,
    Divider,
    useStyleConfig
} from "@chakra-ui/react";

// Defines the mapping between React components and Chakra components.
const mapping = {
    "div": {component: Box},
    "p": {component: Text},
    "a": {component: Link},
    "ul": {component: UnorderedList},
    "ol": {component: OrderedList},
    "li": {component: ListItem},
    "h1": {component: Heading, extra: {as: "h1", size: "2xl"}},
    "h2": {component: Heading, extra: {as: "h2", size: "xl"}},
    "h3": {component: Heading, extra: {as: "h3", size: "lg"}},
    "h4": {component: Heading, extra: {as: "h4", size: "md"}},
    "h5": {component: Heading, extra: {as: "h5", size: "sm"}},
    "h6": {component: Heading, extra: {as: "h6", size: "xs"}},
    "hr": {component: Divider},
    "img": {component: Image},
};

// Defines how to replace HTML nodes with Chakra components.
const options = {
    replace: (node) => {
        const {name, attribs, children, type} = node;

        // Convert `width` & `height` values to `htmlWidth` and `htmlHeight` for Chakra.
        if (name === "img") {
            attribs.htmlWidth = attribs.width;
            attribs.htmlHeight = attribs.height;
            delete attribs.width;
            delete attribs.height;
        }

        if (type === 'script') {
            let script = document.createElement('script');
            script.src = attribs.src;
            document.head.appendChild(script);
        }

        // Add an "element-name" prop to <p> tags so we can check them easily for whitespace processing later.
        let elementName = (name === "p") ? name : undefined;

        // Apply the mapping.
        const obj = mapping[name];
        if (obj) {
            const props = attributesToProps(attribs);
            return React.createElement(
                obj.component,
                {...obj.extra, "element-name": elementName, ...props},
                // Recurse to children if they exist.
                children.length ? domToReact(children, options) : undefined
            );
        }
    }
};

/**
 * Converts raw HTML from Wagtail's RichText to styled, formatted Chakra components.
 *
 * Use the `data` prop to pass in raw HTML.
 * Other props will be applied to the Box around the contents.
 *
 * @param props
 * @constructor
 */
export const RichText = (props) => {
    const {data, ...rest} = props;
    const [parsedData, setParsedData] = useState(null);
    const styles = useStyleConfig('RichText', {});

    useEffect(() => {
        const parsed = parse(data, options);

        // Wagtail doesn't remove trailing <br/>, empty <p></p>, or <p><br></p> tags, which can cause too much trailing whitespace.
        // After the data is parsed, work backwards and remove trailing whitespace until a non-whitespace element is found.
        if (parsed.props && Array.isArray(parsed.props.children)) {
            const children = parsed.props.children;

            let lastNonWhitespaceIndex = 0;
            for (let i = children.length - 1; i >= 0; i--) {
                const {type, props} = children[i];

                if (type === "br") {
                    continue
                }

                if (props && props['element-name'] === "p") {
                    if (!props.children || props.children && props.children.type === "br") {
                        continue
                    }
                }

                lastNonWhitespaceIndex = i + 1;
                break
            }
            children.splice(lastNonWhitespaceIndex)
        }

        setParsedData(parsed);

    }, [data])

    return <Box __css={styles} {...rest}>
        {parsedData}
    </Box>;
};
